import {
  ProductSettingsAffiliatesResponseDto,
  UpdateProductSettingsAffiliatesRequestDto,
} from '@hub-la/sdk-bff-product'
import { Input, Label, RadioGroup, RadioGroupItem, Switch } from '@hub-la/shadcn'
import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { Section } from '../../../components/section'
import { useGetSettings } from '../../../hooks/settings/use-get-settings'
import { useSaveSettings } from '../../../hooks/settings/use-save-settings'

type Values = UpdateProductSettingsAffiliatesRequestDto
const validationSchema = Yup.object().shape({})
const makeInitialValues = (data?: ProductSettingsAffiliatesResponseDto): Values => ({
  enabled: data?.enabled ?? false,
  sell: data?.sell ?? 10,
  renewal: 0,
  public: data?.public ?? false,
})

export const AffiliatesEvent: React.FC = () => {
  const [enabled, setEnabled] = useState<{ sell: boolean; renewal: boolean }>({ sell: true, renewal: false })
  const { productId } = useParams<{ productId: string }>()
  const { mutateAsync: saveSettings } = useSaveSettings()
  const { data, isLoading } = useGetSettings({ productId, type: 'affiliates' })
  const { t } = useTranslation()

  const formik = useFormik<Values>({
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    enableReinitialize: true,
    initialValues: makeInitialValues(data),
    onSubmit: async (values) => {
      await saveSettings({
        productId,
        type: 'affiliates',
        data: {
          ...values,
          sell: enabled.sell ? values.sell : 0,
          renewal: 0,
        },
      })
    },
  })

  useEffect(() => {
    if (data) {
      setEnabled({ sell: true, renewal: false })
    }
  }, [data])

  return (
    <Section
      title={t('settings.affiliatesEvent.title')}
      subtitle={t('settings.affiliatesEvent.subtitle')}
      formik={formik}
    >
      <div className="space-y-6">
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Switch
              id="affiliates-enabled"
              checked={formik.values.enabled}
              onCheckedChange={(checked) => formik.setFieldValue('enabled', checked)}
              disabled={isLoading}
            />
            <Label htmlFor="affiliates-enabled">{t('settings.affiliatesEvent.switch')}</Label>
          </div>
          <p className="text-sm text-muted-foreground">{t('settings.affiliatesEvent.helperText')}</p>
        </div>

        <div className="space-y-3">
          {enabled.sell && (
            <div className="space-y-2">
              <Label htmlFor="sell">{t('settings.affiliatesEvent.input.sell')}</Label>
              <Input
                id="sell"
                type="number"
                value={formik.values.sell}
                onChange={(e) => {
                  const value = Math.min(Number(e.target.value), 90)
                  formik.setFieldValue('sell', value)
                }}
                disabled={isLoading}
                className="max-w-[200px]"
              />
            </div>
          )}
        </div>

        <div className="space-y-3">
          <Label>{t('settings.affiliatesEvent.preferences.title')}</Label>
          <RadioGroup
            onValueChange={(value) => formik.setFieldValue('public', value === 'true')}
            value={formik.values.public.toString()}
            disabled={isLoading}
            className="space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="public-true" />
              <Label htmlFor="public-true">{t('settings.affiliatesEvent.preferences.any')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="public-false" />
              <Label htmlFor="public-false">{t('settings.affiliatesEvent.preferences.members')}</Label>
            </div>
          </RadioGroup>
        </div>
      </div>
    </Section>
  )
}
